export type CookieOptions = {
  maxAgeSeconds: number;
  path?: string;
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
};

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name: string, value: string, opts: CookieOptions): void {
  if (typeof document === 'undefined') return;

  const parts: string[] = [];
  parts.push(`${name}=${encodeURIComponent(value)}`);
  parts.push(`Max-Age=${opts.maxAgeSeconds}`);
  parts.push(`Path=${opts.path ?? '/'}`);
  parts.push(`SameSite=${opts.sameSite ?? 'Lax'}`);
  if (opts.secure) parts.push('Secure');

  document.cookie = parts.join('; ');
}

/**
 * Ensures the cookie has a 1-day TTL WITHOUT extending it on every visit.
 * We do this by storing the last fixed value in a marker cookie.
 *
 * - If target cookie value is same as marker -> do nothing
 * - If value changed (Meta recreated, new fbclid, etc.) -> rewrite with 1-day TTL and update marker
 */
export function enforceOneDayTtlWhenValueChanges(params: {
  targetName: string; // e.g. "_fbc"
  markerValueName: string; // e.g. "_fbc_fixed_value"
  markerAtName?: string; // e.g. "_fbc_fixed_at" (optional)
  oneDaySeconds?: number; // default 86400
  markerTtlSeconds?: number; // default 7 days
  sameSite?: 'Lax' | 'Strict' | 'None';
  secure?: boolean;
}): { changed: boolean; value?: string } {
  const {
    targetName,
    markerValueName,
    markerAtName,
    oneDaySeconds = 60 * 60 * 24,
    markerTtlSeconds = 7 * 60 * 60 * 24,
    sameSite = 'Lax',
    secure = false,
  } = params;

  const value = getCookie(targetName);
  if (!value) return { changed: false };

  const lastFixedValue = getCookie(markerValueName);
  if (lastFixedValue === value) return { changed: false, value };

  // New value => rewrite target cookie to 1-day TTL
  setCookie(targetName, value, {
    maxAgeSeconds: oneDaySeconds,
    sameSite,
    secure,
  });

  // Store marker(s) so we don't extend on daily visits
  setCookie(markerValueName, value, {
    maxAgeSeconds: markerTtlSeconds,
    sameSite,
    secure,
  });

  if (markerAtName) {
    setCookie(markerAtName, String(Math.floor(Date.now() / 1000)), {
      maxAgeSeconds: markerTtlSeconds,
      sameSite,
      secure,
    });
  }

  return { changed: true, value };
}
